import { useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import { useSelector } from "react-redux";

import { useGetSchemaDatasetByAnalysis } from "@/api/analysis/useAnalysisAPI";
import { useGetDatasetMetadataQuery } from "@/api/dataset/useDatasetAPI";
import { SpinnerIcon } from "@/assets/icons";
import { mapAnalysisSchemaToDetailSchema } from "@/helpers/datasetAttributeHelper";
import useOnClickOutside from "@/hooks/ui/useClickOutside";
import { RootState } from "@/store";
import { Dataset, DatasetDetailResponse } from "@/types/dataset";

import { twMerge } from "tailwind-merge";

import EditPopupAttribute from "./EditPopupAttribute";

// Offset dictionary
export const OFFSET_MAPPING = {
  default: { top: 0, left: 320 },
  catchment: { top: -30, left: -110 }
  // can add more if needed
};

interface Props {
  dataset: Dataset;
  anchorEl: HTMLElement;
  offsetId?: keyof typeof OFFSET_MAPPING;
  onClose: () => void;
}

export const EditPopup = ({
  dataset,
  anchorEl,
  offsetId = "default",
  onClose
}: Props) => {
  const { showAnalysisResultPopup } = useSelector(
    (state: RootState) => state.ui
  );
  const { rawDatasets } = useSelector((state: RootState) => state.dataset);
  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);

  const { data: datasetSchema } = useGetSchemaDatasetByAnalysis(
    latestAnalysisId,
    dataset.data.id,
    {
      enabled: showAnalysisResultPopup && !!latestAnalysisId
    }
  );

  const mappedDatasetMetaSchema: Dataset = {
    ...dataset,
    data: {
      ...dataset.data,
      schema: mapAnalysisSchemaToDetailSchema(
        datasetSchema?.schema,
        rawDatasets,
        dataset.data.id
      )
    }
  };

  const dialogRef = useRef<HTMLDialogElement>(null);

  const [overlayElement, setOverlayElement] = useState<HTMLElement | null>(
    null
  );

  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({
    display: "none"
  });

  const { schema: _schema, ...datasetWithoutSchema } = dataset.data;
  const { data: selectedDatasetMetadata } = useGetDatasetMetadataQuery(
    datasetWithoutSchema,
    {
      enabled: !latestAnalysisId && !showAnalysisResultPopup // only fetch when activeDatasetId exists and not showing analysis result popup
    }
  );

  useEffect(() => {
    // Safely access DOM element after component mounts
    setOverlayElement(document.getElementById("overlays"));
  }, []);

  useOnClickOutside(dialogRef, onClose, [overlayElement]);

  const offsetTop = OFFSET_MAPPING[offsetId].top;
  const offsetLeft = OFFSET_MAPPING[offsetId].left;

  // Calculate position only once when the component mounts or anchorEl changes
  useEffect(() => {
    if (!anchorEl) return;

    const rect = anchorEl.getBoundingClientRect();
    // Calculate initial position
    let top = rect.bottom - 20;
    // Get viewport height
    const viewportHeight = window.innerHeight;
    const elementHeight = dialogRef.current?.offsetHeight || 400; // Fallback to estimate

    // Check if element would be clipped by viewport
    const isClippedBottom = top + elementHeight > viewportHeight;

    if (isClippedBottom) {
      top = Math.max(10, viewportHeight - elementHeight - 10); // Position higher
    }

    setPopupStyle({
      display: "block",
      position: "fixed",
      top: top + offsetTop,
      left: rect.left + offsetLeft,
      transform: "none"
    });
  }, [anchorEl, offsetTop, offsetLeft, dialogRef]);

  const datasetOriginal: Dataset = showAnalysisResultPopup
    ? mappedDatasetMetaSchema
    : dataset;
  const selectedDataset: DatasetDetailResponse | null = showAnalysisResultPopup
    ? (mappedDatasetMetaSchema.data ?? null)
    : (selectedDatasetMetadata ?? null);

  return (
    <Draggable nodeRef={dialogRef}>
      <dialog
        ref={dialogRef}
        aria-modal="true"
        style={popupStyle}
        className={twMerge(
          "z-[99] font-sans border-gainsboro bg-white rounded-lg w-[338px]",
          "flex flex-col p-[16px] h-auto max-h-auto"
        )}
      >
        {selectedDataset ? (
          <EditPopupAttribute
            dataset={datasetOriginal}
            selectedDataset={selectedDataset}
            onClose={onClose}
          />
        ) : (
          <SpinnerIcon
            className="my-4 justify-self-center spin-animation"
            title="edit-popup-loading"
            width={60}
            height={60}
          />
        )}
      </dialog>
    </Draggable>
  );
};

export default EditPopup;
